import { createFileRoute, redirect } from '@tanstack/react-router';
import { LoginCard } from '@/components/auth/login-card';
import { ThemeToggle } from '@/components/auth/theme-toggle';
import { useAuthStore } from '@/stores/auth';
import { TooltipProvider } from '@/components/ui/tooltip';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      // Same landing as `/`: the root route re-decides between Dashboard and
      // People off the `home-dashboard` extension switch, so an already
      // signed-in visitor is sent through it rather than to a hardcoded home —
      // hardcoding /dashboard here sent people to a switched-off feature.
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  // Get error from URL params if any
  const searchParams = new URLSearchParams(window.location.search);
  const error = searchParams.get('error');

  const getErrorMessage = (code: string | null): string | null => {
    if (!code) return null;

    const errorMessages: Record<string, string> = {
      DOMAIN_NOT_ALLOWED: 'Sign in is only available for @hygge.software accounts.',
      USER_NOT_FOUND: 'No account found. Contact your administrator to get access.',
      GOOGLE_AUTH_FAILED: 'Google authentication failed. Please try again.',
      ACCOUNT_DISABLED: 'Your account has been disabled. Contact administrator.',
      NETWORK_ERROR: 'Connection error. Please check your internet.',
    };

    return errorMessages[code] || 'An error occurred. Please try again.';
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        {/* Theme toggle - fixed top right */}
        <div className="fixed right-6 top-6 z-50">
          <ThemeToggle />
        </div>

        {/* Background */}
        <div className="fixed inset-0 bg-gradient-to-br from-background via-background to-muted/30" />

        {/* Grid pattern */}
        <div
          className="fixed inset-0 opacity-[0.02] dark:opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Gradient orbs */}
        <div
          className="fixed -right-32 -top-32 h-96 w-96 rounded-full opacity-30 blur-3xl dark:opacity-20"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--hygge)) 0%, hsl(var(--hygge) / 0.5) 100%)',
            animation: 'float 20s ease-in-out infinite',
          }}
        />
        <div
          className="fixed -bottom-32 -left-32 h-80 w-80 rounded-full opacity-20 blur-3xl dark:opacity-15"
          style={{
            background: 'linear-gradient(135deg, hsl(var(--hygge) / 0.5) 0%, hsl(var(--hygge)) 100%)',
            animation: 'float 20s ease-in-out infinite',
            animationDelay: '-10s',
          }}
        />

        {/* Login Card */}
        <LoginCard error={getErrorMessage(error)} />

        {/* Float animation keyframes */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -30px) scale(1.05); }
            66% { transform: translate(-20px, 20px) scale(0.95); }
          }
        `}</style>
      </div>
    </TooltipProvider>
  );
}

